import DocumentEditorPanel from "Areas/Documents/documentEditorPanel";
import { SORT_ORDER } from "Common/dictionaries/sortOrder";
import compact from "lodash/compact";
import head from "lodash/head";
import omit from "lodash/omit";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { FloatSidebarMenuToggler } from "../Common";
import { TAB } from "./constants";
import locale from "./documentsSidebarMenu.locale";
import ScriptEditorPanel from "./scriptEditorPanel";
import TabDocuments from "./tabDocuments";
import TabMethods from "./tabMethods";
import TabNavigationSwitcher from "./tabNavigationSwitcher";
import TabObjects from "./tabObjects";
import TabScripts from "./tabScripts";
import TabTasks from "./tabTasks";
import styles from "./documentsSidebarMenu.module.css";
import classNames from "classnames";
import { Divider } from "Components/Divider";
import find from "lodash/find";
import ButtonHome from "../ButtonHome/ButtonHome";
import HunterLogoFull from "Content/images/logo/HunterLogoFull.svg";
import SidebarMenuTabs from "./sidebarMenuTabs";
import { DocumentNavigation } from "./navigation";
const tabsLocale = {
    [TAB.DOCUMENTS]: locale.DOCUMENTS,
    [TAB.NAVIGATION]: locale.NAVIGATION,
    [TAB.OBJECTS]: locale.OBJECTS,
    [TAB.METHODS]: locale.METHODS,
    [TAB.SCRIPTS]: locale.SCRIPTS,
    [TAB.TASKS]: locale.TASKS
};
const baseTabs = {
    [TAB.NAVIGATION]: {
        key: TAB.NAVIGATION,
        title: tabsLocale[TAB.NAVIGATION],
        icon: "icon-nav"
    },
    [TAB.OBJECTS]: {
        key: TAB.OBJECTS,
        title: tabsLocale[TAB.OBJECTS],
        icon: "icon-object"
    },
    [TAB.TASKS]: {
        key: TAB.TASKS,
        title: tabsLocale[TAB.TASKS],
        icon: "icon-tasks"
    }
};
const tabsComponent = {
    [TAB.OBJECTS]: TabObjects,
    [TAB.NAVIGATION]: TabNavigationSwitcher,
    [TAB.TASKS]: TabTasks,
    [TAB.SCRIPTS]: TabScripts,
    [TAB.METHODS]: TabMethods
};
const availableSortBy = {
    [TAB.DOCUMENTS]: [
        { Key: "sourceData.Created", Name: locale.SORT_BY_CREATION },
        { Key: "title", Name: locale.SORT_BY_TITLE }
    ],
    [TAB.OBJECTS]: [{ Key: "title", Name: locale.SORT_BY_TITLE }],
    [TAB.NAVIGATION]: [{ Key: "title", Name: locale.SORT_BY_TITLE }],
    [TAB.TASKS]: [
        { Key: "Date", Name: locale.SORT_BY_CREATION },
        { Key: "Name", Name: locale.SORT_BY_TITLE }
    ],
    [TAB.METHODS]: [{ Key: "title", Name: locale.SORT_BY_TITLE }],
    [TAB.SCRIPTS]: [
        { Key: "sourceData.Created", Name: locale.SORT_BY_CREATION },
        { Key: "title", Name: locale.SORT_BY_TITLE }
    ]
};
const sortOrderDefault = {
    [TAB.DOCUMENTS]: SORT_ORDER.DESC,
    [TAB.TASKS]: SORT_ORDER.DESC,
    [TAB.SCRIPTS]: SORT_ORDER.DESC
};
export default function DocumentsSidebarMenu(props) {
    const [filterText, setFilterText] = useState("");
    const [sortBy, setSortBy] = useState();
    const [sortOrder, setSortOrder] = useState(SORT_ORDER.ASC);
    const [Component, setComponent] = useState();
    const tabs = useMemo(() => omit(baseTabs, compact([!props.canViewTasks && TAB.TASKS])), []);
    const activeDocument = useMemo(() => find(props.documents, ["Id", props.activeDocumentId]), [props.documents, props.activeDocumentId]);
    useLayoutEffect(() => {
        var _a;
        setFilterText("");
        setSortBy((_a = head(availableSortBy[props.tabKey])) === null || _a === void 0 ? void 0 : _a.Key);
        setSortOrder(sortOrderDefault[props.tabKey] || SORT_ORDER.ASC);
        setComponent(tabsComponent[props.tabKey]);
    }, [props.tabKey]);
    useEffect(() => {
        props.documentType &&
            props.onDocumentTypeChanged &&
            props.onDocumentTypeChanged(props.documentType);
    }, [props.documentType]);
    return (React.createElement("div", { className: classNames(styles.container, props.isExpanded ? styles.expanded : styles.collapsed), "data-sidebar-float-expanded": props.isExpanded },
        React.createElement("div", { className: styles.header },
            React.createElement("div", { className: styles.head },
                React.createElement("a", { href: props.defaultHash },
                    React.createElement("img", { src: HunterLogoFull, alt: "HunterLogoFull", className: styles.logo })),
                React.createElement("div", { className: styles.actions },
                    React.createElement(ButtonHome, { defaultHash: props.defaultHash }),
                    React.createElement(FloatSidebarMenuToggler, { isExpanded: props.isExpanded, onExpandedChanged: props.onExpandedChanged }))),
            React.createElement("div", { className: styles.title },
                React.createElement("h1", { className: styles.name, title: activeDocument === null || activeDocument === void 0 ? void 0 : activeDocument.Name }, activeDocument === null || activeDocument === void 0 ? void 0 : activeDocument.Name),
                (activeDocument === null || activeDocument === void 0 ? void 0 : activeDocument.ProjectName) && (React.createElement("p", { className: styles.project, title: activeDocument.ProjectName }, activeDocument.ProjectName)))),
        React.createElement("div", { className: classNames(styles.documents, !props.isExpanded && styles.hidden) },
            React.createElement(Divider, null),
            React.createElement(TabDocuments, { filterText: filterText, sortBy: sortBy, sortOrder: sortOrder, bgColorDefault: "navbar-bg-item-default", bgColorActive: "navbar-bg-item-active" })),
        React.createElement("div", { className: classNames(styles.tabs, !props.isExpanded && styles.hidden) },
            React.createElement(Divider, null),
            React.createElement(SidebarMenuTabs, { productName: props.productName, rows: props.sidebarMenuTabsRows || 1, expandedMenu: props.isExpanded, tabs: tabs, rotateTab: false }),
            React.createElement(Divider, null)),
        React.createElement("div", { className: classNames(styles["tab-content"], !props.isExpanded && styles.hidden) }, Component && (React.createElement(Component, { filterText: filterText, sortBy: sortBy, sortOrder: sortOrder, bgColorDefault: "navbar-bg-item-default", bgColorActive: "navbar-bg-item-active" }))),
        React.createElement("div", { className: classNames(styles.navigation, !props.isExpanded && styles.hidden) },
            React.createElement(Divider, null),
            React.createElement(DocumentNavigation, null)),
        React.createElement(DocumentEditorPanel, { activeNewDocumentAfterCreation: true }),
        React.createElement(ScriptEditorPanel, null)));
}
